import { ArrowLeft, ArrowRight } from '@mui/icons-material'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  FormHelperText,
  IconButton,
  LinearProgress,
  ListSubheader,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import {
  Campaign,
  ChildAttachment,
  ShareSetting,
} from '@promoboxx/graphql-gateway-types'
import { distance } from 'fastest-levenshtein'
import { Field, FormikContext } from 'formik'
import { TextField } from 'formik-mui'
import I18n from 'i18n-js'
import { useEffect, useState } from 'react'

import { useRetailerAccountLeads } from '@src/components/leads/useLeads'
import retailerSettingsPaths from '@src/components/retailerSettings/retailerSettingsPaths'
import ContentWithTooltip from '@src/components/shared/ContentWithTooltip'
import { useCustomFields } from '@src/customFields/customFieldsContext'
import MissingCustomFieldsAlert from '@src/customFields/MissingCustomFieldsAlert'
import formikConnect from '@src/lib/formikConnect'
import LinkChild from '@src/lib/LinkChild'
import MenuItemWithCheckbox from '@src/lib/MenuItemWithCheckbox'
import { ThemeAwareColor } from '@src/lib/theme'
import useCurrentRetailerAccount from '@src/lib/useCurrentRetailerAccount'
import { useRetailerAccountSocialAccounts } from '@src/lib/useRetailerAccountSocialAccounts'
import { formatDateforDateTimeInput, formatDate } from '@src/lib/Utils'

import OrganicRetailerOverrides from './OrganicRetailerOverrides'

const getMinAndMaxDateString = (campaignEndDate: Campaign['end_date']) => {
  const minDate = new Date(Date.now() + 1000 * 60 * 5) // 5 minutes from now
  let maxDate = new Date()
  maxDate.setFullYear(maxDate.getFullYear() + 1)

  if (campaignEndDate) {
    const campaignEndDateDate = new Date(campaignEndDate)

    if (campaignEndDateDate < maxDate) {
      maxDate = campaignEndDateDate
    }
  }

  return {
    min: minDate.toISOString(),
    max: maxDate.toISOString(),
  }
}

const generateDateStringFromOption = (option: string) => {
  const now = new Date()
  switch (option) {
    case 'later':
      return new Date(now.getTime() + 3 * 1000 * 60 * 60).toISOString()
    case 'tonight':
      return new Date(now.setHours(23, 59)).toISOString()
    case 'tomorrow':
      return new Date(now.setHours(8) + 24 * 1000 * 60 * 60).toISOString()
    default:
      return ''
  }
}

// label is fallback for translation
const scheduleOptions = ['now', 'later', 'tonight', 'tomorrow', 'custom']
const MIN_MESSAGE_LENGTH = 5
const MAX_TWITTER_MESSAGE_LENGTH = 280
export interface ShareSettingDialogFormModel {
  message: string
  link: string
  share_url_type: string
  allow_retailer_url_override: boolean
  child_attachments: Pick<
    ChildAttachment,
    | 'url'
    | 'link'
    | 'allow_retailer_url_override'
    | 'share_url_type'
    | 'description'
    | 'name'
    | 'id'
  >[]
  facebookPageId: string
  twitterAccountId: string
  instagramPageId: string
  scheduled_for: string
  selected_imports: string[]
  subject: string
}

export interface ShareSettingDialogFormValues {
  shareSetting: ShareSetting | undefined | null
  model: ShareSettingDialogFormModel
  carouselIndex: number
  schedulePreset: string
}

export interface ShareSettingFormProps {
  loading: boolean
  formik: FormikContext<ShareSettingDialogFormValues>
  scheduleLabelOverride?: string
}

const ShareSettingForm = formikConnect((props: ShareSettingFormProps) => {
  const shareSetting = props.formik.values.shareSetting
  const numCopyOptions = shareSetting?.share_setting_copy_options?.length || 0
  const [selectedCopyOption, setSelectedCopyOption] = useState(0)
  const socialAccounts = useRetailerAccountSocialAccounts()
  const { leadsImports } = useRetailerAccountLeads()
  const { ensureCurrentRetailerAccount } = useCurrentRetailerAccount()
  const { setBrandId, areAllRequiredFieldsFilled } = useCustomFields()
  const brandId =
    shareSetting?.campaign?.brand_id || shareSetting?.campaign?.brand?.id
  useEffect(() => {
    setBrandId(brandId || null)
  }, [brandId, setBrandId])

  useEffect(() => {
    if (shareSetting?.share_setting_copy_options) {
      setSelectedCopyOption(0)
    }
  }, [shareSetting?.share_setting_copy_options])

  function incrementCopyOption(n: number) {
    setSelectedCopyOption((prev) => {
      let next = prev + n
      return next < 0 ? numCopyOptions - 1 || 0 : next % (numCopyOptions || 1)
    })
  }

  function validateRecipientImportId(id: string[]) {
    if (!id || id.length < 1) {
      return I18n.t('validations.required', {
        field: I18n.t('shareDialog.recipients.label'),
      })
    }
    if (!id.every((x) => x)) {
      return I18n.t('validations.required', {
        field: I18n.t('shareDialog.recipients.label'),
      })
    }
    return
  }

  function validatePageId(id: string) {
    if (!id || id === '-1') {
      return I18n.t('shareDialog.invalidPageId')
    }
    return
  }

  const scheduleLabel =
    props.scheduleLabelOverride || I18n.t('shareDialog.schedule.label')

  let shouldShowOverrides = false
  if (shareSetting?.share_type !== 'email') {
    if (shareSetting?.share_url_type) {
      shouldShowOverrides = true
    }

    if (shareSetting?.child_attachments?.length) {
      shouldShowOverrides = true
    }
  }

  let formBlockingElement: React.ReactNode | null = null

  const selectedCopyMessage =
    shareSetting?.share_setting_copy_options?.[selectedCopyOption]?.message ||
    ''
  const remainingChar =
    MAX_TWITTER_MESSAGE_LENGTH - props.formik.values.model.message.length
  const uniqueness = calculateStringUniqueness(
    selectedCopyMessage,
    props.formik.values.model.message,
  )

  let color: ThemeAwareColor = 'error'
  if (uniqueness > 0.75) {
    color = 'success'
  } else if (uniqueness > 0.25) {
    color = 'warning'
  }

  if (
    shareSetting?.share_type === 'facebook' &&
    socialAccounts.facebookPages.length === 0
  ) {
    formBlockingElement = (
      <>
        <Alert severity="warning">
          <AlertTitle>{I18n.t('shareDialog.noValidAccounts.title')}</AlertTitle>
          {I18n.t('shareDialog.noValidAccounts.message')}
          <br />
          <br />
          <LinkChild
            to={retailerSettingsPaths.socialAccounts.build({
              retailerAccountSlug: ensureCurrentRetailerAccount.slug || '',
            })}
          >
            <Button color="facebook" variant="contained" size="small" fullWidth>
              {I18n.t('shareDialog.noValidAccounts.buttonLabel')}
            </Button>
          </LinkChild>
        </Alert>
      </>
    )
  }

  if (
    shareSetting?.share_type === 'instagram' &&
    socialAccounts.instagramPages.length === 0
  ) {
    formBlockingElement = (
      <>
        <Alert severity="warning">
          <AlertTitle>{I18n.t('shareDialog.noValidAccounts.title')}</AlertTitle>
          {I18n.t('shareDialog.noValidAccounts.message')}
          <br />
          <br />
          <LinkChild
            to={retailerSettingsPaths.socialAccounts.build({
              retailerAccountSlug: ensureCurrentRetailerAccount.slug || '',
            })}
          >
            <Button
              color="instagram"
              variant="contained"
              size="small"
              fullWidth
            >
              {I18n.t('shareDialog.noValidAccounts.buttonLabel')}
            </Button>
          </LinkChild>
        </Alert>
      </>
    )
  }

  if (
    shareSetting?.share_type === 'twitter' &&
    socialAccounts.twitterAccounts.length === 0
  ) {
    formBlockingElement = (
      <>
        <Alert severity="warning">
          <AlertTitle>{I18n.t('shareDialog.noValidAccounts.title')}</AlertTitle>
          {I18n.t('shareDialog.noValidAccounts.message')}
          <br />
          <br />
          <LinkChild
            to={retailerSettingsPaths.socialAccounts.build({
              retailerAccountSlug: ensureCurrentRetailerAccount.slug || '',
            })}
          >
            <Button color="twitter" variant="contained" size="small" fullWidth>
              {I18n.t('shareDialog.noValidAccounts.buttonLabel')}
            </Button>
          </LinkChild>
        </Alert>
      </>
    )
  }

  if (!areAllRequiredFieldsFilled) {
    formBlockingElement = <MissingCustomFieldsAlert brandId={brandId} />
  }

  const { min, max } = getMinAndMaxDateString(shareSetting?.campaign?.end_date)

  return (
    <>
      {props.loading ? (
        <>
          <Skeleton
            // Trying to base this off the height of the Page selector.
            height={78}
          />
          <Skeleton
            // Trying to base this off the height of the TextField.
            height={125}
          />
          <Skeleton
            // Trying to base this off the height of the suggested
            // copy box.
            height={100}
          />
        </>
      ) : formBlockingElement ? (
        <>
          {formBlockingElement}
          {/*
          We need some kind of error in the form to stop submitting. Couldn't
          get `setFieldErrors` to work as expected, but a random field with a
          validation function that always errors does work.
          */}
          <Field
            name="model.facebookPageId"
            component={'input'}
            type="hidden"
            validate={() => {
              return 'form-blocking-element'
            }}
          />
        </>
      ) : (
        <>
          {shareSetting?.share_type === 'email' ? (
            <>
              <Field
                name="model.selected_imports"
                label={I18n.t('shareDialog.recipients.label')}
                placeholder={I18n.t('shareDialog.recipients.placeholder')}
                component={TextField}
                fullWidth
                SelectProps={{ multiple: true }}
                select
                validate={validateRecipientImportId}
              >
                {leadsImports?.map((leadImport) => {
                  return (
                    <MenuItemWithCheckbox
                      value={leadImport.id}
                      key={`import-list-id-${leadImport.id}`}
                    >
                      {leadImport.name}
                    </MenuItemWithCheckbox>
                  )
                })}
              </Field>
              <Field
                name="model.subject"
                label={I18n.t('shareDialog.subject.label')}
                placeholder={I18n.t('shareDialog.subject.placeholder')}
                component={TextField}
                fullWidth
              />
            </>
          ) : undefined}

          {shareSetting?.share_type === 'facebook' ? (
            <Field
              name="model.facebookPageId"
              label={I18n.t('shareDialog.facebookPage')}
              component={TextField}
              fullWidth
              select
              validate={validatePageId}
            >
              {socialAccounts.preferredFacebookPages.length ? (
                <ListSubheader>
                  {I18n.t('shareDialog.preferredPages')}
                </ListSubheader>
              ) : undefined}
              {socialAccounts.preferredFacebookPages.map((x) => {
                return (
                  <MenuItemWithCheckbox value={x.id} key={x.id}>
                    {x.name}
                  </MenuItemWithCheckbox>
                )
              })}
              {socialAccounts.nonPreferredFacebookPages.length ? (
                <ListSubheader>
                  {I18n.t('shareDialog.nonPreferredPages')}
                </ListSubheader>
              ) : undefined}
              {socialAccounts.nonPreferredFacebookPages.map((x) => {
                return (
                  <MenuItemWithCheckbox value={x.id} key={x.id}>
                    {x.name}
                  </MenuItemWithCheckbox>
                )
              })}
            </Field>
          ) : undefined}

          {shareSetting?.share_type === 'twitter' ? (
            <Field
              name="model.twitterAccountId"
              label={I18n.t('shareDialog.twitterAccount')}
              component={TextField}
              fullWidth
              select
              validate={validatePageId}
            >
              {socialAccounts.preferredTwitterAccounts.length ? (
                <ListSubheader>
                  {I18n.t('shareDialog.preferredAccounts')}
                </ListSubheader>
              ) : undefined}
              {socialAccounts.preferredTwitterAccounts.map((x) => {
                return (
                  <MenuItemWithCheckbox value={x.id} key={x.id}>
                    {x.name}
                  </MenuItemWithCheckbox>
                )
              })}
              {socialAccounts.nonPreferredTwitterAccounts.length ? (
                <ListSubheader>
                  {I18n.t('shareDialog.nonPreferredPages')}
                </ListSubheader>
              ) : undefined}
              {socialAccounts.nonPreferredTwitterAccounts.map((x) => {
                return (
                  <MenuItemWithCheckbox value={x.id} key={x.id}>
                    {x.name}
                  </MenuItemWithCheckbox>
                )
              })}
            </Field>
          ) : undefined}

          {shareSetting?.share_type === 'instagram' ? (
            <Field
              name="model.instagramPageId"
              label={I18n.t('shareDialog.instagramAccount')}
              component={TextField}
              fullWidth
              select
              validate={validatePageId}
            >
              {socialAccounts.preferredInstagramPages.length ? (
                <ListSubheader>
                  {I18n.t('shareDialog.preferredPages')}
                </ListSubheader>
              ) : undefined}
              {socialAccounts.preferredInstagramPages.map((x) => {
                return (
                  <MenuItemWithCheckbox value={x.id} key={x.id}>
                    {x.name}
                  </MenuItemWithCheckbox>
                )
              })}
              {socialAccounts.nonPreferredInstagramPages.length ? (
                <ListSubheader>
                  {I18n.t('shareDialog.nonPreferredPages')}
                </ListSubheader>
              ) : undefined}
              {socialAccounts.nonPreferredInstagramPages.map((x) => {
                return (
                  <MenuItemWithCheckbox value={x.id} key={x.id}>
                    {x.name}
                  </MenuItemWithCheckbox>
                )
              })}
            </Field>
          ) : undefined}

          {shareSetting?.share_type !== 'email' &&
            shareSetting?.share_type !== 'embed' && (
              <Field
                name="model.message"
                label={I18n.t('shareDialog.message.label')}
                component={TextField}
                fullWidth
                multiline
                minRows={4}
                maxRows={Infinity}
                helperText={
                  shareSetting?.share_type === 'twitter'
                    ? I18n.t('shareDialog.message.remainingCharacters', {
                        char: remainingChar,
                      })
                    : undefined
                }
                sx={{
                  marginBottom: 0,
                }}
                validate={(message: string) => {
                  if (message.length < MIN_MESSAGE_LENGTH) {
                    return I18n.t('validations.min', {
                      label: I18n.t('shareDialog.message.label'),
                      min: MIN_MESSAGE_LENGTH,
                    })
                  }

                  if (
                    shareSetting?.share_type === 'twitter' &&
                    color === 'error'
                  ) {
                    return I18n.t('shareDialog.uniqueness.tooltip')
                  } else if (
                    shareSetting?.share_type === 'twitter' &&
                    remainingChar < 0
                  ) {
                    return I18n.t('validations.max', {
                      label: I18n.t('shareDialog.message.label'),
                      max: MAX_TWITTER_MESSAGE_LENGTH,
                    })
                  }

                  return
                }}
              />
            )}

          <Box marginY={4}>
            {shareSetting?.share_type === 'twitter' ? (
              <>
                <ContentWithTooltip
                  title={<>{I18n.t('shareDialog.uniqueness.tooltip')}</>}
                >
                  <Typography variant="body2" fontWeight="bold">
                    {I18n.t('shareDialog.uniqueness.label')}
                  </Typography>
                </ContentWithTooltip>

                <LinearProgress
                  variant="determinate"
                  value={uniqueness * 100}
                  color={color}
                />

                {color === 'error' ? (
                  <Alert severity="warning">
                    {I18n.t('shareDialog.uniqueness.helperText')}
                  </Alert>
                ) : undefined}
              </>
            ) : undefined}
          </Box>

          {shareSetting?.share_type !== 'email' &&
            shareSetting?.share_type !== 'embed' && (
              <Paper sx={{ bgcolor: 'grey.100', padding: 2 }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" fontWeight="bold">
                    {I18n.t('shareDialog.suggestedCopy')}
                  </Typography>

                  <span>
                    <IconButton
                      disabled={numCopyOptions === 0 || numCopyOptions === 1}
                      onClick={() => {
                        incrementCopyOption(-1)
                      }}
                      size="small"
                    >
                      <ArrowLeft fontSize="small" />
                    </IconButton>

                    <Typography variant="body2" component="span">
                      {selectedCopyOption + 1} / {numCopyOptions || 1}
                    </Typography>

                    <IconButton
                      disabled={numCopyOptions === 0 || numCopyOptions === 1}
                      onClick={() => {
                        incrementCopyOption(1)
                      }}
                      size="small"
                    >
                      <ArrowRight fontSize="small" />
                    </IconButton>
                  </span>
                </Stack>

                <Typography>
                  {
                    shareSetting?.share_setting_copy_options?.[
                      selectedCopyOption
                    ]?.message
                  }
                </Typography>

                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  size="small"
                  onClick={() => {
                    props.formik.setFieldValue(
                      'model.message',
                      shareSetting?.share_setting_copy_options?.[
                        selectedCopyOption
                      ]?.message || '',
                    )
                  }}
                >
                  {I18n.t('shared.use')}
                </Button>
              </Paper>
            )}

          {shouldShowOverrides ? <OrganicRetailerOverrides /> : undefined}

          {shareSetting?.share_type === 'embed' ? (
            <>
              <Paper sx={{ bgcolor: 'grey.100', padding: 2 }}>
                <Typography variant="body2" fontWeight="bold">
                  {I18n.t('shareDialog.embeds.embed_code.label')}
                </Typography>

                <code style={{ wordWrap: 'break-word' }}>
                  {shareSetting?.share_data?.share_data?.embed_code}
                </code>
              </Paper>

              <FormHelperText>
                {I18n.t('shareDialog.embeds.embed_code.helperText')}
              </FormHelperText>
            </>
          ) : undefined}

          {shareSetting?.share_type !== 'embed' ? (
            props.formik.values.schedulePreset === 'custom' ? (
              <Stack>
                <Field
                  component={TextField}
                  label={scheduleLabel}
                  name="model.scheduled_for"
                  type="datetime-local"
                  // input can't handle iso strings
                  value={formatDateforDateTimeInput(
                    props.formik.values.model.scheduled_for,
                  )}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    title: scheduleLabel,
                    inputProps: {
                      min: min.slice(0, 16),
                      max: max.slice(0, 16),
                    },
                  }}
                  validate={(value: string) => {
                    const date = new Date(value)
                    const label = scheduleLabel

                    if (
                      !value &&
                      props.formik.values.schedulePreset === 'custom'
                    ) {
                      return I18n.t('validations.required', {
                        label,
                      })
                    } else if (isNaN(date.getTime())) {
                      return I18n.t('validations.invalid', {
                        label,
                      })
                    } else if (date < new Date(min)) {
                      return I18n.t('validations.dateMin', {
                        label,
                        min: formatDate(min),
                      })
                    } else if (date > new Date(max)) {
                      return I18n.t('validations.dateMax', {
                        label,
                        max: formatDate(max),
                      })
                    }
                    return
                  }}
                  fullWidth
                />
                <Button
                  onClick={() => {
                    props.formik.setFieldValue('schedulePreset', '')
                    props.formik.setFieldValue('model.scheduled_for', '')
                  }}
                >
                  {I18n.t('shareDialog.schedule.reset')}
                </Button>
              </Stack>
            ) : (
              <Field
                component={TextField}
                label={scheduleLabel}
                name="schedulePreset"
                select
                fullWidth
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  props.formik.setFieldValue('schedulePreset', e.target.value)
                  props.formik.setFieldValue(
                    'model.scheduled_for',
                    generateDateStringFromOption(e.target.value),
                  )
                }}
              >
                {scheduleOptions.map((option) => (
                  <MenuItemWithCheckbox key={option} value={option}>
                    {I18n.t(`shareDialog.schedule.options.${option}`, {
                      default: option,
                    })}
                  </MenuItemWithCheckbox>
                ))}
              </Field>
            )
          ) : undefined}
        </>
      )}
    </>
  )
})

function calculateStringUniqueness(stringA: string, stringB: string) {
  if (!stringA || !stringB) {
    return 0
  }

  const distanceScore = distance(stringA, stringB)

  const longestValue = Math.max(stringA.length, stringB.length)

  const uniqueness = distanceScore / longestValue

  return uniqueness
}

export default ShareSettingForm
